import { CSSSection as Section, Tabs } from "@oxygen/core";
import getThemeClasses from "@oxygen/theme";
import UpcomingCarCarousel from "GlobalComponent/Carousel/UpcomingCarCarousel";
import MediaCampaign from "GlobalComponent/MediaCampaign";
import {
  plClassName,
  prClassName,
  ptClassName,
  pbClassName,
} from "GlobalConstant/ClassNames";
import { PAGE_PROPERTIES } from "GlobalConstant/index";
import { platform as PLATFORM } from "GlobalEnum/Platform";
import { fireInteractiveTracking } from "GlobalUtils/Analytics";
import { processZoneProperty } from "GlobalUtils/Common";
import { ProgressiveHydration } from "GlobalUtils/ProgressiveHydration";
import React, { memo } from "react";
import { NewCarCarousel } from "../components/NewCarCarousel";
import { BEST_CARS_PAGE, NEW_CAR_LAUNCHES_PAGE } from "../constants/index";
import ViewAllLink from "GlobalComponent/ViewAllLink";
import { getUpcomingCarUrl } from "GlobalUtils/UrlFactory";

const UPCOMING_CARS_URL = getUpcomingCarUrl();
const VIEW_ALL_TEXT = {
  POPULAR: "View All Popular Cars",
  JUST_LAUNCHED: "View All Newly Launched Cars",
  UPCOMING: "View All Upcoming Cars",
};

const renderZoneTemplate = (
  zoneCampaign,
  processedTemplateText,
  placeHolder
) => {
  let trackinglbl = "";
  let mediaCampaign = {};
  mediaCampaign.campaignId = zoneCampaign.id;
  if (placeHolder === "title") {
    mediaCampaign.templateText = {};
    mediaCampaign.templateText.html = processedTemplateText.ZoneHeaderhtml;
    trackinglbl = `campaignId_${mediaCampaign.campaignId}_ZoneHeader`;
  }
  mediaCampaign.templateText.script = processedTemplateText.script;
  return (
    <MediaCampaign
      mediaCampaign={mediaCampaign}
      zone={true}
      trackinglbl={trackinglbl}
    />
  );
};

const getPopularCarsTracking = (index, modelName) => ({
  gaCat: "TopSellingCars",
  gaActClick: "TopSelling-Click",
  gaLblClick: modelName,
});

const getMediaProperties = (isZoneAvailable, campaignTemplates) => {
  let zoneCampaign = {};
  if (isZoneAvailable) {
    zoneCampaign = campaignTemplates[PAGE_PROPERTIES.Zone];
  }
  let processedTemplateText = {};
  let mediaCampaign = {};
  if (isZoneAvailable && zoneCampaign && zoneCampaign.name && zoneCampaign.id) {
    processedTemplateText = processZoneProperty(zoneCampaign.name);
    mediaCampaign.templateText = {};
    mediaCampaign.templateText.html = processedTemplateText.ZoneBodyhtml;
    mediaCampaign.campaignId = zoneCampaign.id;
  }
  return { zoneCampaign, processedTemplateText, mediaCampaign };
};
const componentContainerClasses = getThemeClasses(`
  mt-4;
`);
const carouselClasses = getThemeClasses(`
  mt-3 md:mt-4;
`);
const viewAllButtonClasses = getThemeClasses(`
  mt-4 md:mt-6;
  ml-4 md:ml-0;
  mr-4 md:mr-0;
`);
const FeaturedCarWidget = memo(function FeaturedCarWidget({
  popularCars,
  upcomingCars,
  launchedCars,
  openLocationPopup,
  pageName,
  cityId,
  cityName,
  cityMaskingName,
  campaignTemplates,
  upcomingCardText,
  trackCategory,
  showNotifyMe = false,
  platform,
  makeName,
}) {
  const isPopularCarsAvailable = popularCars && popularCars.length > 0;
  const isUpcomingCarsAvailable = upcomingCars && upcomingCars.length > 0;
  const isLaunchedCarsAvailable = launchedCars && launchedCars.length > 0;
  const isZoneAvailable =
    campaignTemplates && campaignTemplates[PAGE_PROPERTIES.Zone];
  if (
    !isPopularCarsAvailable &&
    !isUpcomingCarsAvailable &&
    !isLaunchedCarsAvailable &&
    !isZoneAvailable
  ) {
    return null;
  }
  const isMobile = platform.id === PLATFORM.MOBILE.id;
  const viewAllUpcomingText = upcomingCardText
    ? upcomingCardText
    : VIEW_ALL_TEXT.UPCOMING;
  const {
    zoneCampaign,
    processedTemplateText,
    mediaCampaign,
  } = getMediaProperties(isZoneAvailable, campaignTemplates);

  return (
    <Section data-testing-id="FeaturedCarSection">
      <Section.Meta type="head">
        <Section.Title>{`Featured ${
          makeName ? makeName : ""
        } Cars`}</Section.Title>
      </Section.Meta>
      <Section.Meta
        type="body"
        plClassName={plClassName}
        prClassName={prClassName}
        ptClassName={ptClassName}
      >
        <Tabs
          data-testing-id="featured-cars"
          shouldStick={false}
          isSEO
          tabNavBgColor="transparent"
        >
          {isPopularCarsAvailable && (
            <Tabs.Pane
              title="Popular"
              active
              onTabClick={fireInteractiveTracking.bind(
                null,
                trackCategory,
                "popular_cars_click",
                "PopularCars"
              )}
              data-testing-id="popular-cars-tab-content"
            >
              <div className={componentContainerClasses}>
                <NewCarCarousel
                  dataTestingId="popular-cars"
                  containerClasses={carouselClasses}
                  data={popularCars}
                  openLocationPopup={openLocationPopup}
                  pageName={pageName}
                  platform={platform}
                  slidesPerView={3}
                  showPicLink
                  cityName={cityName}
                  cityMaskingName={cityMaskingName}
                  getTrackingProps={getPopularCarsTracking}
                />
                {isMobile ? (
                  <div className={viewAllButtonClasses}>
                    <ViewAllLink
                      pageUrl={BEST_CARS_PAGE}
                      isAnchorTag
                      platForm={platform}
                      linkText={VIEW_ALL_TEXT.POPULAR}
                      title={VIEW_ALL_TEXT.POPULAR}
                    />
                  </div>
                ) : null}
              </div>
            </Tabs.Pane>
          )}
          {isLaunchedCarsAvailable && (
            <Tabs.Pane
              title="Just Launched"
              onTabClick={fireInteractiveTracking.bind(
                null,
                trackCategory,
                "just_launched_cars_click",
                "JustLaunchedCars"
              )}
              data-testing-id="just-launched-tab-content"
            >
              <ProgressiveHydration as="div">
                <div className={componentContainerClasses}>
                  <NewCarCarousel
                    dataTestingId="just-launched-cars"
                    containerClasses={carouselClasses}
                    data={launchedCars}
                    openLocationPopup={openLocationPopup}
                    pageName={pageName}
                    platform={platform}
                    cardClickAction={`NewLaunchesCardClick_${
                      cityId > 0 ? cityName : "nocity"
                    }`}
                    cardImpAction="NewlyLaunched_Impressions"
                    slidesPerView={3}
                    showPicLink
                    cityName={cityName}
                    cityMaskingName={cityMaskingName}
                  />
                  <div className={viewAllButtonClasses}>
                    <ViewAllLink
                      pageUrl={NEW_CAR_LAUNCHES_PAGE}
                      platForm={platform}
                      linkText={VIEW_ALL_TEXT.JUST_LAUNCHED}
                      title={VIEW_ALL_TEXT.JUST_LAUNCHED}
                    />
                  </div>
                </div>
              </ProgressiveHydration>
            </Tabs.Pane>
          )}
          {isUpcomingCarsAvailable && (
            <Tabs.Pane
              title="Upcoming Cars"
              onTabClick={fireInteractiveTracking.bind(
                null,
                trackCategory,
                "upcoming_cars_click",
                "UpcomingCars"
              )} // todo change later use data attribute
              data-testing-id="upcoming-cars-tab-content"
            >
              <ProgressiveHydration as="div">
                <div className={componentContainerClasses}>
                  <UpcomingCarCarousel
                    containerClassName={carouselClasses}
                    data={upcomingCars}
                    cardText={upcomingCardText}
                    platForm={platform}
                    showNotifyMe={showNotifyMe}
                    slidesPerView={3}
                  />
                  <div className={viewAllButtonClasses}>
                    <ViewAllLink
                      pageUrl={UPCOMING_CARS_URL}
                      platForm={platform}
                      linkText={viewAllUpcomingText}
                      title={viewAllUpcomingText}
                      trackImpression
                      trackClick
                      gaCat={`${pageName}Page`}
                      gaActImp="All_upcoming_Impression"
                      gaLblImp="All_Upcoming_Cars"
                      gaActClick="All_upcoming_Click"
                      gaLblClick="All_Upcoming_Cars"
                    />
                  </div>
                </div>
              </ProgressiveHydration>
            </Tabs.Pane>
          )}
          {isZoneAvailable && (
            <Tabs.Pane
              title={renderZoneTemplate(
                zoneCampaign,
                processedTemplateText,
                "title"
              )}
              onTabClick={fireInteractiveTracking.bind(
                null,
                trackCategory,
                "zone_Title_click",
                "ZoneTitle"
              )}
              data-testing-id="FourthZoneTabContent"
            >
              <MediaCampaign
                mediaCampaign={mediaCampaign}
                zone={true}
                trackinglbl={`campaignId_${mediaCampaign.campaignId}_ZoneBody`}
              />
            </Tabs.Pane>
          )}
        </Tabs>
      </Section.Meta>
    </Section>
  );
});

FeaturedCarWidget.displayName = "FeaturedCarWidget";
export default FeaturedCarWidget;
